import { Button, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Alert, UncontrolledTooltip, InputGroup, InputGroupText, Label } from "reactstrap";
import { useTheme } from "../../../hooks/useTheme";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { getPreSignedUrl, importTemplateTest } from "../data/importTemplateService";
import { downloadFileFromUrl } from "../../../utils/fileUtils";
import { toast } from "react-toastify";
import { getMapToSourceData } from "../data/mapToDataService";
import DataTableWindowed from "../../../components/DataTableWindowed";
import { useQuery } from "@tanstack/react-query";
import { fetchItemLookupByFilter } from "../../items/data/itemLookupService";
import { fetchImportTemplateFieldMappingService } from "../data/importTemplateFieldMappingService";
import CustomSelect from "../../../components/CustomSelect";
import ItemsTable from "../../items/components/ItemsTable";
// NEW: lookups for suppliers and item types
import { fetchSuppliers } from "../../suppliers/data/supplierService";
import { fetchItemLookupType } from "../../items/data/itemLookupTypeService";
import { formatCurrency } from "../../../utils/numberUtils";
import { getBlankQuoteItem } from "../../quotes/functions/quoteFunctions";
import QuoteItemBulkModal from "../../quotes/components/quoteItemBulkModal";
import { fetchQuoteRevisionsDropdown } from '../../quotes/data/quoteRevisionService';
import { fetchOpportunityById } from '../../opportunities/data/opportunitiesService';
import { importQuoteFile } from '../../sharepoint/data/sharepointService';
import { useParams } from 'react-router-dom';
import { createAudit } from "../../../features/audit/data/auditService";
import { downloadFile } from "../../../utils/fileUtils";
import { sanitizeForId } from "../../quotes/functions/quoteFunctions";

// Sentinel value used when the user explicitly chooses to create a new item
const NEW_ITEM_SENTINEL = "__NEW__";

// Core (non-attribute) field keys in camelCase
const CORE_FIELDS = new Set([
    "description", "itemType", "supplier", "autotaskCode", "upfrontSalesforceCode", "recurringSalesforceCode", "quantity", "chargeDescription",
    "isUpfront", "isRecurring", "upfrontCost", "upfrontPrice", "upfrontDescription", "recurringCost", "recurringPrice", "recurringDescription",
    "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments",
    "tags", "id", "name", "options", "isInternal", "isCreditItem", "upfrontRecognitionMonths"
]);

// Curated labels for known core fields
const CORE_LABELS = {
    name: "Internal name",
    itemType: "Item type",
    upfrontSalesforceCode: "Upfront Salesforce code",
    recurringSalesforceCode: "Recurring Salesforce code",
    autotaskCode: "Autotask code",
    upfrontCost: "Upfront cost",
    upfrontPrice: "Upfront price",
    recurringCost: "Recurring cost",
    recurringPrice: "Recurring price",
    recurringStartMonth: "Recurring start month",
    recurringIntervalMonths: "Recurring interval (months)",
    recurringNumPayments: "Recurring # payments",
    recurringDescription: "Recurring description",
    isInternal: "Internal",
    chargeDescription: "Charge description",
    quantity: "Quantity",
    supplier: "Supplier",
    isCreditItem: "Credit Item",
    notes: "Notes"
};

// Pretty-print a camelCase key as a fallback
const prettyFromKey = (k = "") =>
    k.replace(/([A-Z])/g, " $1").replace(/\s+/g, " ").trim()
        .replace(/^./, (c) => c.toUpperCase());

const UserImportTemplateModal = ({ toggle, template, addQuoteItems }) => {
    const theme = useTheme();
    const { id: opportunityId, quoteId, quoteRevisionId } = useParams();
    const revisionId = Number(quoteRevisionId);

    const getRevisions = async () => {
        const revisions = await fetchQuoteRevisionsDropdown(quoteId);
        return revisions;
    }

    const {
        data: revisions = []
    } = useQuery({
        queryKey: ["quoteRevisionsDropdown", quoteId],
        queryFn: getRevisions,
        enabled: !!quoteId,
        staleTime: 60000
    });

    const getOpportunity = async () => {
        const opportunity = await fetchOpportunityById(opportunityId);
        return opportunity?.records;
    }

    const {
        data: opportunity = {}
    } = useQuery({
        queryKey: ["opportunityData", opportunityId],
        queryFn: getOpportunity,
        enabled: !!opportunityId,
        staleTime: 5 * 60_000, // 5 minutes
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const [selectedFile, setSelectedFile] = useState(null);
    const [rows, setRows] = useState([]);                 // raw import rows
    const [columns, setColumns] = useState([]);           // preview of raw rows
    const [isUploading, setIsUploading] = useState(false);
    const [isSavingQuoteItems, setIsSavingQuoteItems] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const fileInputRef = useRef(null);

    const [selectedIds, setSelectedIds] = useState(new Set()); // IDs of rows selected via checkboxes, used for bulk edit modal

    // catalogue search modal
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchRow, setSearchRow] = useState(null);

    // bulk edit modal state
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkItems, setBulkItems] = useState([]);

    // editable group name (pre-filled with a default)
    const [groupName, setGroupName] = useState("");

    const openBulkWithItems = useCallback((items) => {
        setBulkItems(items || []);
        setBulkModalOpen(true);
    }, []);

    const openSearch = useCallback((row) => {
        setSearchRow(row);
        setIsSearchOpen(true);
    }, []);

    const closeSearch = useCallback(() => {
        setIsSearchOpen(false);
        setSearchRow(null);
    }, []);

    // mapped data (catalogue base + import overlay)
    const [mappedData, setMappedData] = useState([]);
    const selectedData = useMemo(() => mappedData.filter(r => selectedIds.has(r.__rowId)), [mappedData, selectedIds]); // the actual row data for selected IDs, passed to bulk edit modal; kept in sync with selectedIds

    const buildDynamicColumns = (preview) => {
        if (!Array.isArray(preview) || preview.length === 0) return [];

        const skipKeys = new Set([
            // internal/meta
            "_importRaw", "_linkItem", "_linkMatchIds", "_linkCandidateIds",
            "_linkSelectedId", "_linkLabel", "_linkStatus", "_linkMatchesCount",
            "__rowId", "order", // we add these ourselves
            // we render these in dedicated columns
            "typeCustomAttributes", "customAttributes",
        ]);

        // Prefer to show these first (and in this order) if present
        const preferred = [
            "catalogueLink",
            "name",
            "itemType",
            "supplier",
            "autotaskCode",
            "salesforceCode",
            "quantity",
            "upfrontCost",
            "upfrontPrice",
            "recurringCost",
            "recurringPrice",
            "recurringStartMonth",
            "recurringIntervalMonths",
            "recurringNumPayments",
            "isInternal",
            "isCreditItem"
        ];

        // Gather keys that actually exist with meaningful values
        const exists = new Set();
        for (const r of preview) {
            Object.entries(r || {}).forEach(([k, v]) => {
                if (!k || skipKeys.has(k) || k.startsWith("_")) return;
                // Hide raw description in favor of Name on Quote
                if (k === "description") return;
                // Show if non-empty OR explicitly false/0
                const nonEmpty = v !== null && v !== undefined && v !== "";
                if (nonEmpty || v === 0 || v === false) exists.add(k);
            });
        }

        // Order: preferred first (that exist), then the rest
        const ordered = [
            ...preferred.filter((k) => exists.has(k)),
            ...[...exists].filter((k) => !preferred.includes(k)),
        ];

        // Width by data-type so narrow fields don't waste space
        const narrowNumeric = new Set([
            "quantity", "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments",
        ]);
        const currencyKeys = new Set([
            "upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice",
        ]);
        const booleanKeys = new Set([
            "isInternal", "isCreditItem", "isUpfront", "isRecurring",
        ]);
        const narrowText = new Set([
            "itemType", "supplier", "salesforceCode"
        ]);

        const widthForKey = (k) => {
            if (narrowNumeric.has(k)) return "100px";
            if (currencyKeys.has(k))  return "180px";
            if (booleanKeys.has(k))   return "80px";            
            if (narrowText.has(k))    return "150px";
            return "250px";
        };

        const dedupedOrdered = Array.from(new Map(ordered.map(k => [k.toLowerCase(), k])).values()); // ✅
        return dedupedOrdered.map(key => ({ key, label: labelForKey(key), 
               style: { width: widthForKey(key), minWidth: widthForKey(key), whiteSpace: "normal" } }));
    };

    const defaultColumns = [
        { key: "select", label: "", render: () => <Input type="checkbox" /> },
        { key: "icon", label: "link", render: () => <i className="bi bi-link" /> },
    ];

    const handleFileDownload = async (key) => {
        const url = await getPreSignedUrl(key);
        if (!url) return;
        if (url.errorMessage) toast.error("Download failed. " + url.errorMessage);
        else await downloadFileFromUrl(url.presignedUrl);
    };

    const toLabel = (k) =>
        (k || "")
            .replace(/_/g, " ")
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/\s+/g, " ")
            .trim()
            .replace(/^./, (c) => c.toUpperCase());

    const buildColumns = (data) => {
        const cols = [];
        const dataKeys = new Set();

        if (Array.isArray(data)) {
            data.forEach((row) =>
                Object.keys(row || {}).forEach((k) => dataKeys.add(k))
            );
        }

        const safeDefaults = defaultColumns.map((c) =>
            dataKeys.has(c.key) ? { ...c, key: `_${c.key}` } : c
        );
        cols.push(...safeDefaults);

        if (!Array.isArray(data) || data.length === 0) return cols;

        cols.push(
            ...Array.from(dataKeys).map((key) => ({
                key,
                label: labelForKey(key),
                render: (value) => {
                    if (value === null || value === undefined) return "";
                    if (typeof value === "object") return JSON.stringify(value);
                    return String(value);
                },
            }))
        );

        return cols;
    };

    const onFileUpload = async (file) => {
        if (!file) return;
        setIsUploading(true);
        setUploadError("");
        setRows([]);
        setColumns([]);

        try {
            const formData = new FormData();
            formData.set("file", file);

            const mapToHeaders = await getMapToSourceData();
            formData.set("configJson", JSON.stringify(template.configJson || {}));
            formData.set("mapToHeaders", JSON.stringify(mapToHeaders || []));

            const jsonData = await importTemplateTest(formData);

            if (jsonData?.error) {
                setUploadError(jsonData.error);
                toast.error(jsonData.error);
                return;
            }

            const data =
                jsonData?.showValidationErrors && Array.isArray(jsonData?.validationErrors)
                    ? jsonData.validationErrors
                    : Array.isArray(jsonData?.entries)
                        ? jsonData.entries
                        : [];

            if (!data.length) {
                setUploadError("No data to display.");
                return;
            }

            setRows(data);
            setColumns(buildColumns(data));
        } catch (err) {
            console.error("Template test failed:", err);
            const msg = "Template test failed.";
            setUploadError(msg);
            toast.error(msg);
        } finally {
            setIsUploading(false);
        }
    };

    const handleHiddenInputChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSelectedFile(file);
        await onFileUpload(file);
    };

    const openPicker = () => fileInputRef.current?.click();

    const handleChangeFile = () => {
        setSelectedFile(null);
        setRows([]);
        setColumns([]);
        setUploadError("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };

    // --- Existing Catalogue (fetch all pages once and cache) ---
    const PAGE_SIZE = 1000000;

    const getFullCatalogue = async () => {
        let pageNumber = 1;
        const all = [];

        // keep pulling pages until we have everything
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const resp = await fetchItemLookupByFilter({
                pageNumber,
                pageSize: PAGE_SIZE,
                onlyShowActiveItems: "Active", // All, Active
            });

            const batch = resp?.itemLookupItems ?? [];
            all.push(...batch);

            if (!resp?.totalRecords || all.length >= resp.totalRecords || batch.length < PAGE_SIZE) break;
            pageNumber += 1;
        }

        return all;
    };

    // data fetch
    const getImportFields = async () => {
        const all = await fetchImportTemplateFieldMappingService();
        return all ?? [];
    };

    const { data: importFieldsData } = useQuery({
        queryKey: ["importFields"],
        queryFn: getImportFields,
        keepPreviousData: true,
        staleTime: 60_000,
    });

    const {
        data: fullCatalogue = [],
    } = useQuery({
        queryKey: ["fullItemCatalogue"],
        queryFn: getFullCatalogue,
        staleTime: 5 * 60 * 1000,
    });

    // NEW: fetch suppliers and item types (for names + dynamic attributes)
    const { data: suppliersData = [] } = useQuery({
        queryKey: ["importSuppliers"],
        queryFn: fetchSuppliers,
        staleTime: 60_000,
    });

    const { data: itemTypes = [] } = useQuery({
        queryKey: ["importItemLookupTypes"],
        queryFn: fetchItemLookupType,
        staleTime: 60_000,
    });

    // --- Lookups & helpers ------------------------------------------------------

    // Keep only one key per case-insensitive name, preferring the canonical form when we know it
    const DEDUPE_CANON = new Map(
        Array.from(CORE_FIELDS).map(k => [String(k).toLowerCase(), k])
    );

    function dedupeCaseKeys(obj) {
        const out = {};
        const seen = new Set(); // lowercased keys we've already emitted

        for (const [k, v] of Object.entries(obj || {})) {
            const lower = String(k).toLowerCase();
            if (seen.has(lower)) continue;

            // if we know a canonical key for this, use it; else keep the first we saw
            const canon = DEDUPE_CANON.get(lower) || k;
            out[canon] = v;
            seen.add(lower);
        }
        return out;
    }

    // Merge strategy: prefer existing non-empty values, else last wins
    const isEmptyVal = (v) =>
        v == null || (typeof v === "string" && v.trim() === "");

    const normalizeRowKeys = (row = {}) => {
        const out = {};
        for (const [rawKey, val] of Object.entries(row || {})) {
            // 1) turn “weird headers” into camel-ish
            const camel = toCamelKey(rawKey);
            // 2) then force to your canonical key name if we know it
            const canon = toCanonicalKey(camel);

            // remember how the user named the column for pretty labels
            registerLabel(canon, rawKey);

            if (!(canon in out) || isEmptyVal(out[canon])) {
                out[canon] = val;
            }
        }
        return out;
    };

    const idToItem = useMemo(() => {
        const m = new Map();
        for (const it of Array.isArray(fullCatalogue) ? fullCatalogue : []) {
            if (it?.id != null) m.set(String(it.id), it);
        }
        return m;
    }, [fullCatalogue]);

    const supplierIdToName = useMemo(() => {
        const m = new Map();
        (suppliersData || []).forEach((s) => m.set(String(s.id), s.supplierName || s.name || `#${s.id}`));
        return m;
    }, [suppliersData]);

    const supplierNameToId = useMemo(() => {
        const m = new Map();
        (suppliersData || []).forEach((s) => {
            const name = String(s.supplierName || s.name || "").trim().toLowerCase();
            if (name) m.set(name, s.id);
        });
        return m;
    }, [suppliersData]);

    const typeIdToName = useMemo(() => {
        const m = new Map();
        (itemTypes || []).forEach((t) => m.set(String(t.id), t.itemLookupTypeName || t.name || `#${t.id}`));
        return m;
    }, [itemTypes]);

    const typeNameToDef = useMemo(() => {
        const m = new Map();
        (itemTypes || []).forEach((t) =>
            m.set(String(t.itemLookupTypeName || t.name || "").trim().toLowerCase(), t)
        );
        return m;
    }, [itemTypes]);

    // util
    const toCamelKey = (s = "") =>
        s
            .replace(/[^a-zA-Z0-9 ]+/g, " ")
            .trim()
            .split(/\s+/)
            .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
            .join("");

    // !!! HOISTED: normalizeAttrCollection used below
    function normalizeAttrCollection(input) {
        if (input == null || input === "") return [];
        let arr = input;
        if (typeof arr === "string") {
            try { arr = JSON.parse(arr); } catch { return []; }
        }
        if (!Array.isArray(arr)) return [];
        return arr
            .map((x) => ({
                label: String(x?.label ?? x?.name ?? x?.key ?? "").trim(),
                value: x?.value ?? null,
            }))
            .filter((x) => x.label);
    }

    // Collect attribute labels from ITEM TYPES
    const typeAttrLabelMap_fromTypes = useMemo(() => {
        const m = new Map();
        (itemTypes || []).forEach((t) =>
            (t?.typeCustomAttributes || []).forEach((a) => {
                const key = toCamelKey(a?.label ?? a?.name ?? a?.key ?? "");
                const label = String(a?.label ?? a?.name ?? a?.key ?? "").trim();
                if (key) m.set(key, label);
            })
        );
        return m;
    }, [itemTypes]);

    // FINAL: union of (types ∪ catalogue ∪ template)
    const typeAttrLabelMap = useMemo(() => {
        const merged = new Map();
        for (const src of [typeAttrLabelMap_fromTypes]) {
            for (const [k, v] of src.entries()) if (!merged.has(k)) merged.set(k, v);
        }
        return merged;
    }, [typeAttrLabelMap_fromTypes]);

    const typeAttrLabelKeys = useMemo(() => new Set(typeAttrLabelMap.keys()), [typeAttrLabelMap]);

    // Build a lowercase->canonical map for known keys
    const CANON_KEY_MAP = useMemo(() => {
        const toCanon = new Map();

        // Start with your curated/core fields
        CORE_FIELDS.forEach((k) => {
            toCanon.set(String(k).toLowerCase(), k);
        });

        // canonicalize any attribute labels that become keys
        typeAttrLabelMap.forEach((_, camelKey) => {
            toCanon.set(String(camelKey).toLowerCase(), camelKey);
        });

        toCanon.set("type", "itemType"); // special case

        return toCanon;
    }, [typeAttrLabelMap]);

    const toCanonicalKey = (k) => CANON_KEY_MAP.get(String(k || "").toLowerCase()) || k;


    // Pick the version with the greatest activeStartDate <= now; tie-break by createdAt
    const getCurrentVersion = (versions, now = new Date()) => {
        if (!Array.isArray(versions) || versions.length === 0) return null;
        const nowTime = +now;
        let best = null;
        for (const v of versions) {
            const start = new Date(v.activeStartDate).getTime();
            if (Number.isNaN(start) || start > nowTime) continue;
            if (!best) { best = v; continue; }
            const bestStart = new Date(best.activeStartDate).getTime();
            if (start > bestStart) best = v;
            else if (start === bestStart) {
                const created = new Date(v.createdAt).getTime() || 0;
                const bestCreated = new Date(best.createdAt).getTime() || 0;
                if (created > bestCreated) best = v;
            }
        }
        return best;
    };

    const labelForItem = (item) => {
        if (!item) return "";
        const base =
            item.name ||
            item.autotaskCode ||
            item.salesforceCode ||
            `#${item.id}`;
        return item.options ? `${base}, ${item.options}` : base;
    };

    const coerceBooleans = (v) => {
        if (typeof v === "string") v = v.trim().toLowerCase();
        return v === true || v === "true" || v === "yes" || v === "y" || v === "1" || v === 1;
    };

    const deriveBillingType = (row) => {
        const up = !!row.isUpfront;
        const rec = !!row.isRecurring;
        return billingTypeFromFlags(up, rec);
    };

    // Which fields are link keys, per template
    const linkFieldKeys = useMemo(() => {
        const sheets = template?.configJson?.sheets ?? [];
        const cols = sheets.flatMap((s) => s?.columns ?? []);
        return cols
            .filter((c) => c?.isLink)
            .map((c) => toCamelKey(c.mapTo))
            .filter(Boolean);
    }, [template]);

    const catalogueIndex = useMemo(() => {
        const idx = {};
        for (const k of linkFieldKeys) idx[k] = new Map();
        for (const item of Array.isArray(fullCatalogue) ? fullCatalogue : []) {
            for (const k of linkFieldKeys) {
                const raw = item?.[k];
                if (raw == null) continue;
                const v = String(raw).trim();
                const bucket = idx[k].get(v);
                if (bucket) bucket.push(item);
                else idx[k].set(v, [item]);
            }
        }
        return idx;
    }, [fullCatalogue, linkFieldKeys]);

    const linkRowExact = (row) => {
        const activeKeys = linkFieldKeys.filter((k) => {
            const rv = row?.[k];
            return rv != null && String(rv).trim() !== "";
        });
        if (activeKeys.length === 0) return { status: "NO_ROW_VALUES" };

        let candidatesMap = null;
        for (const k of activeKeys) {
            const v = String(row[k]).trim();
            const arr = catalogueIndex[k]?.get(v) ?? [];
            if (candidatesMap == null) {
                candidatesMap = new Map(arr.map((it) => [it.id, it]));
            } else {
                const idsThisKey = new Set(arr.map((it) => it.id));
                for (const id of Array.from(candidatesMap.keys())) {
                    if (!idsThisKey.has(id)) candidatesMap.delete(id);
                }
            }
            if (candidatesMap.size === 0) break;
        }

        const matches = candidatesMap ? Array.from(candidatesMap.values()) : [];
        if (matches.length === 1)
            return { status: "OK", id: matches[0].id, item: matches[0], label: labelForItem(matches[0]) };
        if (matches.length > 1) return { status: "CONFLICT", count: matches.length, ids: matches.map((m) => m.id) };
        return { status: "NOT_FOUND" };
    };

    // --- Merge helpers (import wins; catalogue fills blanks) ---
    const isEmpty = (v) =>
        v == null ||
        (typeof v === "string" && v.trim() === "") ||
        (Array.isArray(v) && v.length === 0);

    const toNumOrNull = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };

    const firstNonEmpty = (...vals) => vals.find((v) => !isEmpty(v));

    // Billing type label helper (allows both)
    const billingTypeFromFlags = (up, rec) =>
        up && rec ? "Mixed" : up ? "Upfront" : rec ? "Recurring" : "Unassigned";

    const inferBillingFromValues = (raw, version) => {
        const hasRecurring =
            coerceBooleans(raw?.isRecurring) ||
            ["recurringCost", "recurringPrice", "recurringPeriod", "recurringDescription", "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments"].some(
                (k) => !isEmpty(raw?.[k])
            );

        const hasUpfront =
            coerceBooleans(raw?.isUpfront) ||
            ["upfrontCost", "upfrontPrice", "upfrontDescription"].some((k) => !isEmpty(raw?.[k]));

        if (hasRecurring && hasUpfront) return "Mixed";
        if (hasRecurring && !hasUpfront) return "Recurring";
        if (!hasRecurring && hasUpfront) return "Upfront";

        if (version) {
            if (version.isRecurring && !version.isUpfront) return "Recurring";
            if (!version.isRecurring && version.isUpfront) return "Upfront";
            if (version.isRecurring && version.isUpfront) return "Mixed";
        }

        return "Unassigned";
    };

    const normAttrLabel = (s) => String(s ?? "").trim();

    const mergeAttrsByLabel = (a = [], b = []) => {
        const map = new Map();
        for (const x of a) {
            const k = normAttrLabel(x.label).toLowerCase();
            if (!k) continue;
            map.set(k, { label: normAttrLabel(x.label), value: x.value ?? null });
        }
        for (const x of b) {
            const k = normAttrLabel(x.label).toLowerCase();
            if (!k) continue;
            map.set(k, { label: normAttrLabel(x.label), value: x.value ?? null });
        }
        return Array.from(map.values());
    };

    const parseAttrColumnsFromRow = (row, baseName) => {
        if (!row) return [];
        const out = [];
        const dot = new RegExp(`^${baseName}\\.([^:.[\\]]+)$`, "i");
        const brk = new RegExp(`^${baseName}\\[([^\\]]+)\\]$`, "i");
        const col = new RegExp(`^${baseName}:([^:.[\\]]+)$`, "i");
        for (const [k, v] of Object.entries(row)) {
            if (v == null || v === "") continue;
            const key = String(k);
            const m = key.match(dot) || key.match(brk) || key.match(col);
            if (m && m[1]) out.push({ label: normAttrLabel(m[1]), value: v });
        }
        return out;
    };

    const slugKey = (s) =>
        String(s).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

    const blankCatalogueForType = (raw) => {
        const tName = String(raw?.itemType || raw?.Type || "").trim().toLowerCase();
        const t = tName ? typeNameToDef.get(tName) : null;
        const typeAttrs = Array.isArray(t?.typeCustomAttributes) ? t.typeCustomAttributes : [];
        const mappedTypeAttrs = typeAttrs
            .map((a) => ({
                label: String(a?.label ?? a?.name ?? a?.key ?? "").trim(),
                value: null,
            }))
            .filter((x) => x.label);

        return {
            id: null,
            description: null,
            itemType: t ? (t.itemLookupTypeName || t.name) : (raw?.itemType ?? null),
            supplier: null,
            autotaskCode: null,
            salesforceCode: null,
            upfrontSalesforceCode: null,
            recurringSalesforceCode: null,
            quantity: null,
            isInternal: null,
            isCreditItem: null,
            isUpfront: null,
            isRecurring: null,
            billingType: "Unassigned",
            chargeDescription: "",
            customAttributes: [],
            typeCustomAttributes: mappedTypeAttrs,
        };
    };

    const mergeableKeys = useMemo(() => {
        const sheets = template?.configJson?.sheets ?? [];
        const cols = sheets.flatMap((s) => s?.columns ?? []);
        const fromTemplate = cols.map((c) => c?.mapTo).filter(Boolean);
        const canonical = fromTemplate.map((x) => toCamelKey(x));

        const extras = [
            "description", "itemType", "supplier", "autotaskCode", "upfrontSalesforceCode", "recurringSalesforceCode", "quantity", "chargeDescription",
            "isUpfront", "isRecurring", "upfrontCost", "upfrontPrice", "upfrontDescription", "recurringCost", "recurringPrice", "recurringDescription",
            "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments", "customAttributes", "typeCustomAttributes", "isInternal", "isCreditItem"
        ];

        return Array.from(new Set([...canonical, ...extras]));
    }, [template]);

    const labelRegistryRef = useRef(new Map()); // canonKey -> friendly label
    const registerLabel = (canonKey, rawKey) => {
        if (!canonKey) return;
        if (labelRegistryRef.current.has(canonKey)) return;

        // Prefer curated; else prefer the original header text; else fallback pretty camel
        const rawFriendly = String(rawKey || "").trim();
        const label =
            CORE_LABELS[canonKey] ||
            (rawFriendly ? rawFriendly : prettyFromKey(canonKey));

        labelRegistryRef.current.set(canonKey, label);
    };

    const labelForKey = (key) =>
        CORE_LABELS[key] ||
        labelRegistryRef.current.get(key) ||
        prettyFromKey(key);

    const applyCatalogueFallbackOnRow = useCallback((row) => {
        const cat = row?._linkItem || (row?.id ? idToItem.get(String(row.id)) : null);
        const raw = row?._importRaw ?? {};
        const version = getCurrentVersion(cat?.itemLookupVersions || []);

        const base = blankCatalogueForType(raw);

        if (cat) {
            base.description = cat.name ?? cat.description ?? base.description;

            const typeNameFromCat =
                cat.itemLookupTypeName ??
                (cat.itemLookupType && cat.itemLookupType.itemLookupTypeName) ??
                cat.typeName ??
                cat.itemType ??
                typeIdToName.get(String(cat.itemLookupTypeId ?? cat.typeId)) ??
                null;
            if (typeNameFromCat) base.itemType = typeNameFromCat;

            const supplierNameFromCat =
                cat.supplierName ??
                (cat.supplier && (cat.supplier.supplierName || cat.supplier.name)) ??
                supplierIdToName.get(String(cat.supplierId ?? cat.vendorId)) ??
                cat.vendor ??
                cat.supplier ??
                null;
            if (supplierNameFromCat) base.supplier = supplierNameFromCat;

            base.autotaskCode = cat.autotaskCode ?? base.autotaskCode;
            base.quantity = (cat.defaultQuantity ?? cat.quantity ?? base.quantity);

            if (typeof cat.isInternal === "boolean") {
                base.isInternal = cat.isInternal;
            }

            if (typeof cat.isCreditItem === "boolean") {
                base.isCreditItem = cat.isCreditItem;
            }

            const catTypeAttrs = normalizeAttrCollection(
                (version && version.typeCustomAttributes) ?? cat.typeCustomAttributes
            );
            const catCustAttrs = normalizeAttrCollection(
                (version && version.customAttributes) ?? cat.customAttributes
            );
            if (catTypeAttrs.length) base.typeCustomAttributes = catTypeAttrs;
            if (catCustAttrs.length) base.customAttributes = catCustAttrs;

            if (version) {
                base.isUpfront = !!version.isUpfront;
                base.isRecurring = !!version.isRecurring;
                base.upfrontNameOnQuote = version.upfrontNameOnQuote ?? base.upfrontNameOnQuote;
                base.recurringNameOnQuote = version.recurringNameOnQuote ?? base.recurringNameOnQuote;

                if (version.isRecurring && !version.isUpfront) {
                    base.billingType = "Recurring";
                    base.recurringCost = toNumOrNull(version.recurringCost);
                    base.recurringPrice = toNumOrNull(version.recurringPrice);
                    base.chargeDescription = version.quoteRecurringDescription ?? base.chargeDescription;
                    base.recurringStartMonth = toNumOrNull(version.recurringStartMonth) ?? toNumOrNull(base.recurringStartMonth);
                    base.recurringIntervalMonths = toNumOrNull(version.recurringIntervalMonths) ?? toNumOrNull(base.recurringIntervalMonths);
                    base.recurringNumPayments = toNumOrNull(version.recurringNumPayments) ?? toNumOrNull(base.recurringNumPayments);
                    base.recurringSalesforceCode = version.recurringSalesforceCode ?? base.recurringSalesforceCode;
                } else if (!version.isRecurring && version.isUpfront) {
                    base.billingType = "Upfront";
                    base.upfrontCost = toNumOrNull(version.upfrontCost);
                    base.upfrontPrice = toNumOrNull(version.upfrontPrice);
                    base.chargeDescription = version.quoteUpfrontDescription ?? base.chargeDescription;
                    base.upfrontSalesforceCode = version.upfrontSalesforceCode ?? base.upfrontSalesforceCode;
                }else if (version.isRecurring && version.isUpfront) {
                    base.billingType = "Mixed";
                    base.upfrontCost = toNumOrNull(version.upfrontCost);
                    base.upfrontPrice = toNumOrNull(version.upfrontPrice);
                    base.upfrontSalesforceCode = version.upfrontSalesforceCode ?? base.upfrontSalesforceCode;
                    base.recurringCost = toNumOrNull(version.recurringCost);
                    base.recurringPrice = toNumOrNull(version.recurringPrice);
                    base.chargeDescription = version.quoteUpfrontDescription ?? version.quoteRecurringDescription ?? base.chargeDescription;
                    base.recurringStartMonth = toNumOrNull(version.recurringStartMonth) ?? toNumOrNull(base.recurringStartMonth);
                    base.recurringIntervalMonths = toNumOrNull(version.recurringIntervalMonths) ?? toNumOrNull(base.recurringIntervalMonths);
                    base.recurringNumPayments = toNumOrNull(version.recurringNumPayments) ?? toNumOrNull(base.recurringNumPayments);
                    base.recurringSalesforceCode = version.recurringSalesforceCode ?? base.recurringSalesforceCode;
                }
            }

            base.salesforceCode = base.upfrontSalesforceCode && base.recurringSalesforceCode
                ? `${base.upfrontSalesforceCode} / ${base.recurringSalesforceCode}`
                : base.upfrontSalesforceCode ?? base.recurringSalesforceCode ?? null;
        }

        // --- IMPORT overlay (wins) ---
        if (!isEmpty(raw.description)) base.description = raw.description;
        if (!isEmpty(raw.itemType)) {
            base.itemType = raw.itemType;
            const t = typeNameToDef.get(String(raw.itemType).trim().toLowerCase());
            if (t && (!base.typeCustomAttributes || base.typeCustomAttributes.length === 0)) {
                base.typeCustomAttributes = normalizeAttrCollection(t.typeCustomAttributes);
            }
        }
        if (!isEmpty(raw.supplier)) base.supplier = raw.supplier;
        if (!isEmpty(raw.autotaskCode)) base.autotaskCode = raw.autotaskCode;
        if (!isEmpty(raw.quantity)) base.quantity = toNumOrNull(raw.quantity);


        if (raw && Object.prototype.hasOwnProperty.call(raw, "isInternal") && raw.isInternal != null) {
            base.isInternal = coerceBooleans(raw.isInternal);
        }
        else if (row?._userEdited?.isInternal === true && row.hasOwnProperty("isInternal")) {
            base.isInternal = coerceBooleans(row.isInternal);
        }

        if (raw && Object.prototype.hasOwnProperty.call(raw, "isCreditItem") && raw.isCreditItem != null) {
            base.isCreditItem = coerceBooleans(raw.isCreditItem);
        }
        else if (row?._userEdited?.isCreditItem === true && row.hasOwnProperty("isCreditItem")) {
            base.isCreditItem = coerceBooleans(row.isCreditItem);
        }

        const importTypeAttrs = normalizeAttrCollection(raw.typeCustomAttributes);
        if (importTypeAttrs.length) base.typeCustomAttributes = importTypeAttrs;

        const importCustAttrs = normalizeAttrCollection(raw.customAttributes);
        if (importCustAttrs.length) base.customAttributes = importCustAttrs;

        // Decide flags independently (allow both)
        let up = false;
        let rec = false;

        // 1) Direct booleans from row/import
        if (row?.isUpfront === true || coerceBooleans(raw?.isUpfront)) up = true;
        if (row?.isRecurring === true || coerceBooleans(raw?.isRecurring)) rec = true;

        // 2) If still undecided, look at version flags
        if (!up && !rec && version) {
            if (version.isUpfront) up = true;
            if (version.isRecurring) rec = true;
        }

        // 3) If still undecided, infer from side values
        if (!up || !rec) {
            const fromValues = inferBillingFromValues(raw, null); // ignore version here
            if (fromValues === "Mixed") { up = true; rec = true; }
            else if (fromValues === "Upfront") up = true;
            else if (fromValues === "Recurring") rec = true;
        }

        // 4) Default: if neither was decisive, pick Upfront (same as before)
        if (!up && !rec) up = true;

        base.isUpfront = up;
        base.isRecurring = rec;
        base.billingType = billingTypeFromFlags(up, rec);

        // Apply import values per side (overlay on any version defaults above)
        if (!isEmpty(raw.upfrontCost)) base.upfrontCost = toNumOrNull(raw.upfrontCost);
        if (!isEmpty(raw.upfrontPrice)) base.upfrontPrice = toNumOrNull(raw.upfrontPrice);
        if (!isEmpty(raw.recurringCost)) base.recurringCost = toNumOrNull(raw.recurringCost);
        if (!isEmpty(raw.recurringPrice)) base.recurringPrice = toNumOrNull(raw.recurringPrice);
        if (!isEmpty(raw.recurringStartMonth)) base.recurringStartMonth = toNumOrNull(raw.recurringStartMonth);
        if (!isEmpty(raw.recurringIntervalMonths)) base.recurringIntervalMonths = toNumOrNull(raw.recurringIntervalMonths);
        if (!isEmpty(raw.recurringNumPayments)) base.recurringNumPayments = toNumOrNull(raw.recurringNumPayments);
        if (!isEmpty(raw.upfrontDescription)) base.upfrontDescription = raw.upfrontDescription;
        if (!isEmpty(raw.upfrontSalesforceCode)) base.upfrontSalesforceCode = raw.upfrontSalesforceCode;
        if (!isEmpty(raw.recurringSalesforceCode)) base.recurringSalesforceCode = raw.recurringSalesforceCode;
        if (!isEmpty(raw.recurringDescription)) base.recurringDescription = raw.recurringDescription;
        if (!isEmpty(raw.chargeDescription)) base.chargeDescription = raw.chargeDescription;

        // --- USER OVERRIDES (from bulk modal) ---
        const preferUser = (key) => row?._userEdited?.[key] === true;

        // Simple scalar fields
        ["description", "itemType", "supplier", "autotaskCode", "upfrontSalesforceCode", "recurringSalesforceCode", "quantity", "isInternal", "isCreditItem",
            "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments"].forEach(k => {
                if (preferUser(k)) base[k] = row[k];
            });

        // If user forced billing flags/types, keep them
        if (preferUser("billingType")) {
            base.billingType = row.billingType || base.billingType;
            base.isUpfront = base.billingType === "Upfront";
            base.isRecurring = base.billingType === "Recurring";
        }
        if (preferUser("isUpfront") || preferUser("isRecurring")) {
            if (row.isUpfront === true && row.isRecurring !== true) {
                base.billingType = "Upfront";
                base.isUpfront = true;
                base.isRecurring = false;
            } else if (row.isRecurring === true && row.isUpfront !== true) {
                base.billingType = "Recurring";
                base.isUpfront = false;
                base.isRecurring = true;
            }
        }

        base.salesforceCode = base.upfrontSalesforceCode && base.recurringSalesforceCode
            ? `${base.upfrontSalesforceCode} / ${base.recurringSalesforceCode}`
            : base.upfrontSalesforceCode ?? base.recurringSalesforceCode ?? null;

        // Pricing & descriptions: keep explicit side edits
        if (preferUser("upfrontCost")) base.upfrontCost = toNumOrNull(row.upfrontCost);
        if (preferUser("upfrontPrice")) base.upfrontPrice = toNumOrNull(row.upfrontPrice);
        if (preferUser("recurringCost")) base.recurringCost = toNumOrNull(row.recurringCost);
        if (preferUser("recurringPrice")) base.recurringPrice = toNumOrNull(row.recurringPrice);
        if (preferUser("recurringStartMonth")) base.recurringStartMonth = toNumOrNull(row.recurringStartMonth);
        if (preferUser("recurringIntervalMonths")) base.recurringIntervalMonths = toNumOrNull(row.recurringIntervalMonths);
        if (preferUser("recurringNumPayments")) base.recurringNumPayments = toNumOrNull(row.recurringNumPayments);
        if (preferUser("upfrontDescription")) base.upfrontDescription = row.upfrontDescription ?? "";
        if (preferUser("recurringDescription")) base.recurringDescription = row.recurringDescription ?? "";

        // Reflect the chosen billing into cost/price display fields
        // If user edited a side description, prefer it for chargeDescription
        if (preferUser("upfrontDescription") && !isEmpty(base.upfrontDescription)) {
            base.chargeDescription = base.upfrontDescription;
        }
        if (preferUser("recurringDescription") && !isEmpty(base.recurringDescription)) {
            base.chargeDescription = base.recurringDescription;
        }

        // Type custom attributes: if user edited in bulk modal, keep them
        if (preferUser("typeCustomAttributes")) {
            base.typeCustomAttributes = normalizeAttrCollection(row.typeCustomAttributes);
        }

        return { ...row, ...base };
    }, [idToItem, typeNameToDef, supplierIdToName, typeIdToName, itemTypes, mergeableKeys]);

    // --- Map to new data structure ---
    useEffect(() => {
        if (!Array.isArray(rows) || rows.length === 0) {
            setMappedData([]);
            return;
        }

        const md = rows.map((r, i) => {
            const src = normalizeRowKeys(r);
            let out = { ...src, __rowId: i, order: i + 1 };
            out = dedupeCaseKeys(out); // nuke case-dup keys

            out._importRaw = {};
            for (const k of mergeableKeys) {
                out._importRaw[k] = src?.[k] ?? null;
            }

            // look at available type custom attribute labels
            // and promote any scalar keys that match into typeCustomAttributes
            const allLabels = itemTypes.flatMap(itemType => {
                try {
                    const customAttributes = JSON.parse(itemType.customAttributes || '[]');
                    return customAttributes.map(attr => attr.label);
                } catch (e) {
                    console.warn('Invalid JSON in customAttributes:', itemType.customAttributes);
                    return [];
                }
            });

            // attribute exists within allowed type attributes then register it
            Object.keys(src).forEach((k) => {
                const canonKey = toCanonicalKey(k);
                registerLabel(canonKey, k);

                // normalize key for comparison
                const normalizedKey = k.replace(/\s+/g, '').toLowerCase();
                const matchedItem = allLabels.find(x => x.replace(/\s+/g, '').toLowerCase() === normalizedKey)

                if (matchedItem && !canonKey.startsWith("_")) {
                    src.typeCustomAttributes = src.typeCustomAttributes || [];
                    src.typeCustomAttributes.push({ label: matchedItem, value: src[k] });
                    delete out[k]; // remove from root level so it doesn't render
                }
            });

            // Attributes from arrays/column-style keys
            const tacFromArray = normalizeAttrCollection(src.typeCustomAttributes);
            const tacFromCols = parseAttrColumnsFromRow(src, "typeCustomAttributes");

            // explicit (array/columns) first
            const explicitTypeAttrs = mergeAttrsByLabel(tacFromArray, tacFromCols);
            const explicitLabelSet = new Set(explicitTypeAttrs.map(a => normAttrLabel(a.label).toLowerCase()));

            // Scalar → type-attr if key matches any known attribute label (types, catalogue, or template)
            const tacFromScalar = Object.entries(src).flatMap(([k, v]) => {
                if (v == null || v === "") return [];
                const keyNorm = toCamelKey(k);
                if (!typeAttrLabelKeys.has(keyNorm)) return [];
                // Do not promote any canonical core fields into type attributes
                if (CORE_FIELDS.has(keyNorm)) return [];
                const label = typeAttrLabelMap.get(keyNorm) || normAttrLabel(k);
                if (explicitLabelSet.has(normAttrLabel(label).toLowerCase())) return [];
                return [{ label, value: v }];
            });

            // IMPORT precedence: explicit wins over scalar; then we store into _importRaw
            out._importRaw.typeCustomAttributes = mergeAttrsByLabel(
                tacFromScalar,
                explicitTypeAttrs
            );

            // Optional: general custom attributes
            const cacFromArray = normalizeAttrCollection(src.customAttributes);
            const cacFromCols = parseAttrColumnsFromRow(src, "customAttributes");
            out._importRaw.customAttributes = mergeAttrsByLabel(cacFromArray, cacFromCols);

            // coercions
            if (typeof out.tags === "string") {
                out.tags = out.tags.split(",").map((s) => s.trim()).filter(Boolean);
            }
            ["quantity", "upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice", "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments"].forEach((k) => {
                const n = Number(out[k]);
                out[k] = Number.isFinite(n) ? n : (out[k] ?? null);
            });
            ["isCreditItem", "isInternal", "isUpfront", "isRecurring"].forEach((k) => {
                if (k in out) out[k] = coerceBooleans(out[k]);
            });

            // derive billing quick flag
            out.billingType = deriveBillingType(out);

            // exact link
            const link = linkRowExact(out);
            if (link.status === "OK") {
                out.id = link.id;
                out._linkStatus = "OK";
                out._linkMatchesCount = 1;
                out._linkLabel = link.label;
                out._linkItem = link.item;
                out._linkCandidateIds = [String(link.id)];
                out.itemType = r.itemType ?? r.typeName ?? r.itemLookupTypeName ?? link.itemLookupTypeName;
            } else if (link.status === "CONFLICT") {
                out.id = null;
                out._linkStatus = "CONFLICT";
                out._linkMatchesCount = link.count;
                out._linkMatchIds = link.ids?.map(String);
                out._linkSelectedId = "";
                out._linkCandidateIds = [...(out._linkMatchIds || [])];
            } else {
                out.id = null;
                out._linkStatus = "NEW";
                out._linkSelectedId = NEW_ITEM_SENTINEL;
                out._linkCandidateIds = [];
            }

            return applyCatalogueFallbackOnRow(out);
        });

        setMappedData(md);
    }, [
        rows, fullCatalogue, linkFieldKeys, catalogueIndex, mergeableKeys,
        typeNameToDef, typeIdToName, supplierIdToName,
        typeAttrLabelKeys, typeAttrLabelMap, itemTypes
    ]);

    const resolveLinkForRow = useCallback(
        (rowId, selectedIdOrNull) => {
            setMappedData((prev) =>
                prev.map((r) => {
                    if (r.__rowId !== rowId) return r;

                    const selectedId = selectedIdOrNull ?? "";

                    if (selectedId === NEW_ITEM_SENTINEL) {
                        const next = { ...r };
                        next.id = null;
                        next._linkItem = null;
                        next._linkLabel = null;
                        next._linkStatus = "NEW";
                        next._linkSelectedId = NEW_ITEM_SENTINEL;
                        // When explicitly creating a new item, default recurrence to Upfront
                        next.billingType = "Upfront";
                        next.isUpfront = true;
                        next.isRecurring = false;
                        return applyCatalogueFallbackOnRow(next);
                    }

                    const sel = selectedId ? idToItem.get(String(selectedId)) : null;
                    const next = { ...r };

                    if (sel) {
                        next.id = sel.id;
                        next._linkItem = sel;
                        next._linkLabel = labelForItem(sel);
                        next._linkStatus = "OK";
                        next._linkMatchesCount = 1;
                        next._linkSelectedId = String(sel.id);

                        // keep import/user-entered values (do NOT null itemType/supplier)

                        const set = new Set([...(next._linkCandidateIds || []), String(sel.id)]);
                        (next._linkMatchIds || []).forEach((id) => set.add(String(id)));
                        next._linkCandidateIds = Array.from(set);

                        // Align flags to the linked item's current version (can be both)
                        const v = getCurrentVersion(sel?.itemLookupVersions || []);
                        const up = !!v?.isUpfront;
                        const rec = !!v?.isRecurring;
                        next.isUpfront = up;
                        next.isRecurring = rec;
                        next.billingType = billingTypeFromFlags(up, rec);

                        return applyCatalogueFallbackOnRow(next);
                    } else {
                        next.id = null;
                        next._linkItem = null;
                        next._linkLabel = null;
                        next._linkSelectedId = "";
                        next._linkStatus = (next._linkMatchIds?.length ?? 0) > 0 ? "CONFLICT" : "NOT_FOUND";
                        return applyCatalogueFallbackOnRow(next);
                    }
                })
            );
        },
        [idToItem]
    );

    useEffect(() => {
        if (!mappedData?.length) return;
        setMappedData((prev) => prev.map((r) => applyCatalogueFallbackOnRow(r)));
    }, [idToItem, itemTypes, suppliersData, applyCatalogueFallbackOnRow]);

    // Build a set of allowed type attribute labels based on the item type definitions
    const allowedTypeAttrLabels = useMemo(() => {
        const s = new Set();
        (itemTypes || []).forEach((t) => {
            const parsedCustomAttributes = JSON.parse(t?.customAttributes || "[]");
            (parsedCustomAttributes || []).forEach((a) => {
                const lbl = normAttrLabel(a?.label ?? a?.name ?? a?.key ?? "");
                if (lbl) s.add(lbl.toLowerCase());
            });
        });
        return s;
    }, [itemTypes]);

    // --- dynamic attribute columns (restricted to defined typeCustomAttributes) ---
    const typeAttrColumns = useMemo(() => {

        const allLabels = itemTypes.flatMap(itemType => {
            try {
                const customAttributes = JSON.parse(itemType.customAttributes || '[]');
                return customAttributes.map(attr => attr.label);
            } catch (e) {
                console.warn('Invalid JSON in customAttributes:', itemType.customAttributes);
                return [];
            }
        });

        // Ensure labels are unique
        const uniqueLabels = [...new Set(allLabels)];

        // Filter out columns that have no values in mappedData
        const labelsWithValues = uniqueLabels.filter(label =>
            mappedData.some(row =>
                (row.typeCustomAttributes || []).some(a => a.label === label && a.value?.toString().trim() !== "")
            )
        );

        return Array.from(labelsWithValues).map((label) => {
            const key = `typeAttr__${slugKey(label)}`;
            return {
                key,
                label: `Attr::${label}`,
                style: { whiteSpace: "normal" },
                searchValue: (_, row) =>
                    // This is a virtual column, so we need to pull the value from typeCustomAttributes for searching/sorting
                    String((row.typeCustomAttributes || []).find((a) => a.label === label)?.value ?? ""),
                render: (_, row) => {
                    const v = (row.typeCustomAttributes || [])
                        .find((a) => a.label === label);
                    return v?.value ?? "";
                },
            };
        });
    }, [mappedData, allowedTypeAttrLabels, itemTypes]);

    const customAttrColumns = useMemo(() => {
        const labels = new Set();
        for (const r of mappedData || []) {
            (r.customAttributes || []).forEach((a) => {
                const lbl = normAttrLabel(a.label);
                if (lbl) labels.add(lbl);
            });
        }
        return Array.from(labels).map((label) => {
            const key = `custAttr__${slugKey(label)}`;
            return {
                key,
                label: `Attr • ${label}`,
                style: { whiteSpace: "normal" },
                render: (_, row) => {
                    const v = (row.customAttributes || [])
                        .find((a) => normAttrLabel(a.label).toLowerCase() === label.toLowerCase());
                    return v?.value ?? "";
                },
            };
        });
    }, [mappedData]);

    const mappedColumns = useMemo(() => {
        // Start with dynamic keys discovered in the data
        const base = buildDynamicColumns(mappedData);

        const orderCol = {
            key: "order", 
            label: "Order",
            isFixed: true,
            style: { width: "30px" },
            group: "Selection",
            className: "text-center text-muted"
        };

        // Enhance special fields with custom renders (currency, catalogue link, etc.)
        const byKey = Object.fromEntries(base.map(c => [c.key, { ...c  }]));

        // Ensure catalogueLink exists (dynamic builder may include it if present; add if missing)
        if (!byKey.catalogueLink) {
            byKey.catalogueLink = { key: "catalogueLink", label: "Catalogue", isFixed: true,
            style: { width: "370px", minWidth: "370px" },
            group: "Selection" };
        }
        byKey.catalogueLink.render = (_, row) => {
            const status = row?._linkStatus;
            const idAttr = `catlink-${row.__rowId}`;
            const label = row?._linkLabel || "New Item";

            const candidateIds = Array.from(
                new Set(
                    [
                        ...(row._linkCandidateIds || []),
                        ...((row._linkMatchIds || []).map(String)),
                    ].filter(Boolean)
                )
            );

            const optionFromId = (cid) => {
                const it = idToItem.get(String(cid));
                const lbl = it
                    ? (it.name || `#${it.id}`) + (it.options ? `, ${it.options}` : "")
                    : String(cid) === String(row.id) && row._linkLabel
                        ? row._linkLabel
                        : `#${cid}`;
                return { value: String(cid), label: lbl };
            };

            const options = [
                ...candidateIds.map(optionFromId),
                { value: NEW_ITEM_SENTINEL, label: "New Item" },
            ];

            const selectValue = row._linkSelectedId
                ? String(row._linkSelectedId)
                : (status === "NEW" || status === "NOT_FOUND" || status === "NO_ROW_VALUES")
                    ? NEW_ITEM_SENTINEL
                    : "";

            if (["OK", "NEW", "NOT_FOUND", "NO_ROW_VALUES"].includes(status)) {
                const tooltipId = `${idAttr}-tip`;
                return (
                    <div className="d-flex align-items-center gap-2">
                        <span id={tooltipId} className="d-inline-flex align-items-center gap-1">
                            <i
                                className={`bi ${status === "NEW"
                                    ? "bi-plus-circle text-primary"
                                    : status === "OK"
                                        ? "bi-link-45deg text-success"
                                        : "bi-search text-muted"
                                    }`}
                            />
                        </span>
                        <div style={{ minWidth: 280 }}>
                            <CustomSelect
                                isMulti={false}
                                options={options}
                                value={selectValue}
                                placeholder={
                                    status === "NEW"
                                        ? "New Item"
                                        : status === "OK"
                                            ? "Linked item"
                                            : "Choose item…"
                                }
                                onChange={(opt) => {
                                    resolveLinkForRow(row.__rowId, opt ? opt.value : null);
                                }}
                            />
                        </div>
                        <Button
                            id={`${idAttr}-searchbtn`}
                            color="link"
                            size="sm"
                            className="p-0"
                            onClick={() => openSearch(row)}
                        >
                            <i className="bi bi-search" />
                            <span className="visually-hidden">Search catalogue</span>
                        </Button>
                        <UncontrolledTooltip placement="top" target={`${idAttr}-searchbtn`}>
                            Search catalogue
                        </UncontrolledTooltip>
                        <UncontrolledTooltip placement="top" target={tooltipId}>
                            {status === "NEW" ? (
                                <div className="text-start">
                                    <div><strong>New item</strong></div>
                                    <div>This row will be created as a new item.</div>
                                </div>
                            ) : status === "OK" && row?._linkItem ? (
                                <div className="text-start">
                                    {(row._linkItem.name) && (
                                        <div><strong>{row._linkItem.name}</strong></div>
                                    )}
                                    <div>ID: {row._linkItem.id}</div>
                                    {row._linkItem.options && <div>Options: {row._linkItem.options}</div>}
                                    {row._linkItem.autotaskCode && <div>Autotask: {row._linkItem.autotaskCode}</div>}
                                    {row._linkItem.salesforceCode && <div>Salesforce: {row._linkItem.salesforceCode}</div>}
                                    {typeof row._linkItem.isInternal === "boolean" && (
                                        <div>Internal: {row._linkItem.isInternal ? "Yes" : "No"}</div>
                                    )}
                                    {typeof row._linkItem.isCreditItem === "boolean" && (
                                        <div>Credit Item: {row._linkItem.isCreditItem ? "Yes" : "No"}</div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-start">
                                    <div><strong>No exact match</strong></div>
                                    <div>Select “New Item” to create during import.</div>
                                </div>
                            )}
                        </UncontrolledTooltip>
                    </div>
                );
            }

            if ((row?._linkMatchIds?.length ?? 0) > 0) {
                const tooltipId = `${idAttr}-tip`;
                const optionsWithNew = [
                    ...candidateIds.map((cid) => optionFromId(cid)),
                    { value: NEW_ITEM_SENTINEL, label: "New Item" },
                ];
                return (
                    <div className="d-flex align-items-center gap-2">
                        <span id={tooltipId} className="d-inline-flex align-items-center gap-1">
                            <i className="bi bi-exclamation-triangle-fill text-danger" />
                        </span>
                        <div style={{ minWidth: 280 }}>
                            <CustomSelect
                                isMulti={false}
                                isClearable
                                options={optionsWithNew}
                                value={row._linkSelectedId || row.id ? String(row._linkSelectedId || row.id) : ""}
                                placeholder={row?._linkMatchesCount ? `Conflict (${row._linkMatchesCount})` : "Conflict"}
                                onChange={(opt) => {
                                    resolveLinkForRow(row.__rowId, opt ? opt.value : null);
                                }}
                            />
                        </div>
                        <Button
                            id={`${idAttr}-searchbtn`}
                            color="link"
                            size="sm"
                            className="p-0"
                            onClick={() => openSearch(row)}
                        >
                            <i className="bi bi-search" />
                            <span className="visually-hidden">Search catalogue</span>
                        </Button>
                        <UncontrolledTooltip placement="top" target={`${idAttr}-searchbtn`}>
                            Search catalogue
                        </UncontrolledTooltip>
                        <UncontrolledTooltip placement="top" target={tooltipId}>
                            <div className="text-start">
                                <div><strong>Multiple matches</strong></div>
                                <div>Pick one, or choose “New Item”.</div>
                            </div>
                        </UncontrolledTooltip>
                    </div>
                );
            }

            const iconClass =
                status === "OK"
                    ? "bi-link-45deg text-success"
                    : status === "CONFLICT"
                        ? "bi-exclamation-triangle-fill text-danger"
                        : status === "NEW"
                            ? "bi-plus-circle text-primary"
                            : "bi-search text-muted";

            const cellText =
                status === "OK"
                    ? label
                    : status === "CONFLICT"
                        ? `Conflict (${row?._linkMatchesCount})`
                        : "New Item";

            return (
                <span id={idAttr} className="d-inline-flex align-items-center gap-1">
                    <i className={`bi ${iconClass} me-2`} />
                    <span>{cellText}</span>
                </span>
            );
        };

        // Currency fields 
        for (const k of ["upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice"]) {
            if (byKey[k]) {
                byKey[k] = {
                    ...byKey[k],
                    className: "p-1",
                    style: { width: "180px", minWidth: "180px" },
                    render: (_, row) => (
                        <InputGroup>
                            <InputGroupText>£</InputGroupText>
                            <Input disabled className="px-3 text-end" type="text" value={formatCurrency(row[k])} />
                        </InputGroup>
                    ),
                };
            }
        }

        // Narrow numeric fields
        for (const k of ["quantity", "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments"]) {
            if (byKey[k]) {
                byKey[k] = {
                    ...byKey[k],
                    className: "p-1 text-end",
                    style: { width: "100px", minWidth: "100px", whiteSpace: "normal" },
                };
            }
        }

        // isInternal checkbox
        if (byKey.isInternal) {
            byKey.isInternal = {
                ...byKey.isInternal,
                className: "text-center",
                style: { width: "80px", minWidth: "80px" },
                render: (value, row) => (
                    <Input
                        type="checkbox"
                        checked={!!row.isInternal}
                        onChange={(e) => {
                            const nextVal = !!e.target.checked;
                            setMappedData(prev =>
                                prev.map(r =>
                                    r.__rowId === row.__rowId
                                        ? applyCatalogueFallbackOnRow({
                                            ...r,
                                            isInternal: nextVal,
                                            _userEdited: { ...(r._userEdited || {}), isInternal: true }
                                        })
                                        : r
                                )
                            );
                        }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Mark as internal"
                    />
                ),
            };
        }

        if (byKey.isCreditItem) {
            byKey.isCreditItem = {
                ...byKey.isCreditItem,
                className: "text-center",
                style: { width: "80px", minWidth: "80px" },
                render: (value, row) => (
                    <Input
                        type="checkbox"
                        checked={!!row.isCreditItem}
                        onChange={(e) => {
                            const nextVal = !!e.target.checked;
                            setMappedData(prev =>
                                prev.map(r =>
                                    r.__rowId === row.__rowId
                                        ? applyCatalogueFallbackOnRow({
                                            ...r,
                                            isCreditItem: nextVal,
                                            _userEdited: { ...(r._userEdited || {}), isCreditItem: true }
                                        })
                                        : r
                                )
                            );
                        }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Mark as credit"
                    />
                ),
            };
        }

        // Actions column at the end
        const actionsCol = {
            key: "actions",
            label: "",
            group: "",
            isSystem: true,
            isDefault: true,
            isFixed: true,
            style: { width: "50px" },
            fixDirection: "right",
            className: "p-1 text-end",
            render: (_, row) => (
                <Button onClick={() => openBulkWithItems([row])}>
                    <i className="bi bi-pencil" />
                </Button>
            ),
        };

        // Merge final list: select → catalogueLink → dynamic columns → type/custom attrs → actions
        const orderedKeys = [
            "catalogueLink",
            ...Object.keys(byKey).filter(k => k !== "catalogueLink" && k !== "order"),
        ];
        const dynamicCols = orderedKeys.map(k => byKey[k]).filter(Boolean);

        const allColumns = [
            ...([orderCol, ...dynamicCols].map(c => ({ ...c, group: c.group !== undefined ? c.group : "Main" }))),
            ...([...typeAttrColumns, ...customAttrColumns].map(c => ({ ...c, group: "Attributes" }))),
            actionsCol
        ].map(c => ({ ...c, isDefault: true }));

        return allColumns
    }, [
        mappedData,                   // critical: recompute when data changes
        openBulkWithItems, applyCatalogueFallbackOnRow,
        formatCurrency, typeAttrColumns, customAttrColumns,
        idToItem, resolveLinkForRow, typeNameToDef, openSearch]);


    // Decide billing for a mapped row in a single place
    const decideBilling = (row) => {
        // 1) Explicit label
        if (row.billingType === "Mixed") return { isUpfront: true, isRecurring: true };
        if (row.billingType === "Upfront") return { isUpfront: true, isRecurring: false };
        if (row.billingType === "Recurring") return { isUpfront: false, isRecurring: true };

        // 2) Booleans from row
        const upFlag = row.isUpfront === true;
        const recFlag = row.isRecurring === true;
        if (upFlag || recFlag) return { isUpfront: upFlag, isRecurring: recFlag };

        // 3) Infer from values (both if both present)
        const hasUp = Number(row.upfrontPrice ?? row.upfrontCost) > 0;
        const hasRec = Number(row.recurringPrice ?? row.recurringCost) > 0;
        if (hasUp || hasRec) return { isUpfront: !!hasUp, isRecurring: !!hasRec };

        // 4) Default
        return { isUpfront: true, isRecurring: false };
    };

    const handleAddItem = useCallback(
        (itemOrId) => {
            const id =
                typeof itemOrId === "object"
                    ? itemOrId?.id ?? itemOrId?.Id ?? itemOrId?.ID
                    : itemOrId;
            if (id == null || searchRow == null) {
                setIsSearchOpen(false);
                return;
            }

            const idStr = String(id);

            if (idToItem.has(idStr)) {
                resolveLinkForRow(searchRow.__rowId, idStr);
            } else {
                const fallbackLabel =
                    typeof itemOrId === "object" && (itemOrId.name)
                        ? itemOrId.name
                        : `#${idStr}`;

                setMappedData((prev) =>
                    prev.map((r) => {
                        if (r.__rowId !== searchRow.__rowId) return r;
                        const next = { ...r };
                        next.id = id;
                        next._linkStatus = "OK";
                        next._linkSelectedId = idStr;
                        next._linkLabel = fallbackLabel;
                        next._linkItem = null;
                        next._linkMatchesCount = 1;
                        const set = new Set([...(next._linkCandidateIds || []), idStr]);
                        (next._linkMatchIds || []).forEach((cid) => set.add(String(cid)));
                        next._linkCandidateIds = Array.from(set);
                        return applyCatalogueFallbackOnRow(next);
                    })
                );
            }

            setIsSearchOpen(false);
        },
        [idToItem, searchRow, resolveLinkForRow]
    );


    const handleImport = async () => {

        if (!groupName || groupName.trim() === "") {
            toast.error("Please enter a quote group name before importing.");
            return;
        }

        const mappedDataWithLinksResolved = mappedData.map(row => {
            // Prefer explicit supplierId; otherwise derive from supplier name (case-insensitive)
            let derivedSupplierId = row.supplierId ?? null;
            if (!derivedSupplierId) {
                const supplierNameCandidate =
                    (row?.supplier && String(row.supplier)) ||
                    (row?.supplierName && String(row.supplierName)) ||
                    (row?._linkItem?.supplierName && String(row._linkItem.supplierName)) ||
                    null;

                if (supplierNameCandidate) {
                    const key = supplierNameCandidate.trim().toLowerCase();
                    const lookedUp = supplierNameToId.get(key);
                    if (lookedUp != null) derivedSupplierId = lookedUp;
                }
            }

            const itemLookupTypeId = row.itemLookupTypeId ??
                (row.itemType
                    ? (typeNameToDef.get(String(row.itemType).trim().toLowerCase())?.id ?? null)
                    : null);

            return {
                ...row,
                supplierId: derivedSupplierId,
                itemLookupTypeId: itemLookupTypeId
            }
        })

        const rowsMissingItemType = mappedDataWithLinksResolved.filter(x => !x.itemLookupTypeId)

        if (rowsMissingItemType.length > 0) {
            toast.error(`${rowsMissingItemType.length} row(s) are missing an item type. Please fix before importing.`);
            return;
        }

        const rowsMissingSupplier = mappedDataWithLinksResolved.filter(x => !x.supplierId)
        if (rowsMissingSupplier.length > 0) {
            toast.error(`${rowsMissingSupplier.length} row(s) are missing a supplier. Please fix before importing.`);
            return;
        }

        /*
            If we got this far, then assume we have all required info to actually save the items
        */

        toggle()

        setIsSavingQuoteItems(true)

        //var groupName = "Import " + formatDateTime(new Date())

        const newQuoteItems = []

        mappedDataWithLinksResolved.forEach(importItem => {
            const { isUpfront, isRecurring } = decideBilling(importItem)
            var newItem = getBlankQuoteItem()

            newItem.quantity = importItem.quantity
            newItem.quoteGroupDescription = groupName
            newItem.notes = importItem.notes || ""
            newItem.internalName = importItem.name || importItem.upfrontDescription || importItem.recurringDescription || "Unknown"
            newItem.autotaskCode = importItem.autotaskCode || ""            
            newItem.upfrontSalesforceCode = isUpfront ? (importItem.upfrontSalesforceCode || null) : null
            newItem.upfrontRecognitionMonths = isUpfront ? (importItem.upfrontRecognitionMonths ?? null) : null
            newItem.recurringSalesforceCode = isRecurring ? (importItem.recurringSalesforceCode || null) : null
            const upCode = newItem.upfrontSalesforceCode;
            const recCode = newItem.recurringSalesforceCode;
            newItem.salesforceCode = upCode && recCode && upCode !== recCode
                ? `${upCode} / ${recCode}`
                : upCode || recCode || null;
            newItem.source = "Import";
            newItem.importTemplateId = template.id;
            newItem.supplierId = importItem.supplierId;
            newItem.itemLookupTypeId = importItem.itemLookupTypeId;  
            newItem.itemLookupId = importItem._linkItem?.id; // optional, but if present can link back to the source item
            newItem.itemLookupVersionId = importItem._linkItem?.itemLookupVersions ? getCurrentVersion(importItem._linkItem.itemLookupVersions)?.id : null;           
            newItem.isInternal = !!importItem.isInternal;
            newItem.isCreditItem = !!importItem.isCreditItem;

            newItem.isUpfront = isUpfront
            newItem.isRecurring = isRecurring
            newItem.billingType = billingTypeFromFlags(isUpfront, isRecurring)

            const num = (v) => (v == null || v === "" ? 0 : Number(v) || 0)
            const upPrice = num(importItem.upfrontPrice)
            const upCost = num(importItem.upfrontCost)
            const recPrice = num(importItem.recurringPrice)
            const recCost = num(importItem.recurringCost)

            newItem.originalRecurringPrice = isRecurring ? recPrice : 0
            newItem.recurringPrice = isRecurring ? recPrice : 0
            newItem.originalUpfrontPrice = isUpfront ? upPrice : 0
            newItem.upfrontPrice = isUpfront ? upPrice : 0

            newItem.originalRecurringCost = isRecurring ? recCost : 0
            newItem.recurringCost = isRecurring ? recCost : 0
            newItem.originalUpfrontCost = isUpfront ? upCost : 0
            newItem.upfrontCost = isUpfront ? upCost : 0
            // Description → correct quote field (mirrors Cost/Price split)
            // Prefer chargeDescription, then the specific side, then the generic description
            const baseDesc =
                importItem.chargeDescription ||
                importItem.description ||
                importItem.name ||
                "";

            if (isRecurring) {
                newItem.recurringNumPayments = importItem.recurringNumPayments ?? null;
                newItem.recurringIntervalMonths = importItem.recurringIntervalMonths ?? null;
                newItem.recurringStartMonth = importItem.recurringStartMonth ?? null;

                newItem.recurringNameOnQuote =
                    importItem.recurringDescription || baseDesc;
                if (!isUpfront) newItem.upfrontNameOnQuote = "";
            }

            if (isUpfront) {
                newItem.upfrontNameOnQuote =
                    importItem.upfrontDescription || baseDesc;
                if (!isRecurring) newItem.recurringNameOnQuote = "";
            }

            // If neither Upfront nor Recurring is set (unlikely), fall back to upfront
            if (!isUpfront && !isRecurring) {
                newItem.upfrontNameOnQuote = importItem.upfrontDescription || baseDesc;
                newItem.recurringNameOnQuote = "";
            }

            if (isRecurring) {
                /* TODO: If these are available on the import, then perhaps can set them.  Otherwise you can do so via the UI */

                newItem.recurringNumPayments = importItem.recurringNumPayments ?? null;
                newItem.recurringIntervalMonths = importItem.recurringIntervalMonths ?? null;
                newItem.recurringStartMonth = importItem.recurringStartMonth ?? null;

            }

            // ---- Type Custom Attributes ----
            // ---- Type Custom Attributes (coerce booleans based on the item type schema) ----
            const selectedTypeId = newItem.itemLookupTypeId;

            // find the item type definition that applies to this row
            const typeDef = (itemTypes || []).find(t => String(t.id) === String(selectedTypeId));

            // parse that type's attribute schema
            let typeAttrDefs = [];
            try {
                typeAttrDefs = typeDef?.customAttributes ? JSON.parse(typeDef.customAttributes) : [];
            } catch { /* ignore bad JSON */ }

            // collect labels that are boolean for quick lookup
            const booleanLabels = new Set(
                (typeAttrDefs || [])
                    .filter(def => String(def.fieldType).toLowerCase() === "boolean")
                    .map(def => String(def.label || def.name || def.key || "").trim().toLowerCase())
            );

            // coerce values to real booleans for those labels
            const coercedTypeAttrs = (importItem.typeCustomAttributes || []).map(a => {
                const label = String(a?.label ?? "").trim();
                let value = a?.value;

                if (booleanLabels.has(label.toLowerCase())) {
                    value = coerceBooleans(value); // uses your existing helper: true/"true"/"yes"/"1" → true
                }

                return { label, value };
            });

            newItem.typeCustomAttributes = coercedTypeAttrs;

            newQuoteItems.push(newItem);
        });

        addQuoteItems(newQuoteItems);

        // Upload file to sharepoint
        const blob = new Blob([selectedFile], {
            type: selectedFile.type
        });

        const revision = revisions.find(f => f.id === revisionId);
        const revisionNumber = revision?.revisionNumber;
        if (!revisionNumber) {
            toast.error(`Quote ${quoteId} - could not determine revision number for SharePoint upload.`);
            setIsSavingQuoteItems(false);
            return;
        }

        const safeOriginalName = String(selectedFile?.name ?? "import").replace(/[\\/:*?"<>|#%&{}~]/g, "-").trim();
        const fileName = `${quoteId}-${revisionNumber} ${safeOriginalName}`;
        const sharepointFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

        const form = new FormData();
        form.append('file', sharepointFile);
        form.append('sharepoint', opportunity.SharePoint_Folder__c);
        form.append('quoteId', quoteId);
        form.append('revisionNumber', revisionNumber);
        const { message, responseOk } = await importQuoteFile(form);
        if (!responseOk) {
            toast.error(`${message} ${sharepointFile.name}`);
            console.error(`Quote ${quoteId} revision ${revision.revisionNumber} - Error uploading the import file ${sharepointFile.name} to Sharepoint: ${message}`);
            await createAudit({ "action": "Upload sharepoint import file", "result": "Error", "source": `quote.${quoteId}`, "payload": JSON.stringify({ message: `Error uploading the import file ${sharepointFile.name} to Sharepoint: ${message}` }) });
        }
        else {
            await createAudit({ "action": "Upload sharepoint import file", "result": "Successful", "source": `quote.${quoteId}`, "payload": JSON.stringify({ sharepointFile: message }) });
        }

        setIsSavingQuoteItems(false)
    }

    const handleBulkSave = useCallback((updatedRows) => {
        const byId = new Map((updatedRows || []).map(r => [r.__rowId, r]));

        // fields we want to respect as user overrides when changed
        const EDITABLE_FIELDS = new Set([
            "name", "displayName", "description", "quantity", "isInternal", "isCreditItem",
            "supplier", "supplierId", "itemType", "itemLookupTypeId",
            "autotaskCode", "upfrontSalesforceCode", "recurringSalesforceCode",
            "isUpfront", "isRecurring", "billingType",
            "upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice",
            "upfrontDescription", "recurringDescription",
            "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments",
            "typeCustomAttributes",
        ]);

        setMappedData(prev =>
            prev.map(orig => {
                const upd = byId.get(orig.__rowId);
                if (!upd) return orig;

                // compute per-field changes to set _userEdited flags
                const userEdited = { ...(orig._userEdited || {}) };
                for (const k of Object.keys(upd)) {
                    if (!EDITABLE_FIELDS.has(k)) continue;
                    const oldVal = orig[k];
                    const newVal = upd[k];
                    const changed = JSON.stringify(oldVal ?? null) !== JSON.stringify(newVal ?? null);
                    if (changed) userEdited[k] = true;
                }

                const merged = { ...orig, ...upd, _userEdited: userEdited };
                return applyCatalogueFallbackOnRow(merged);
            })
        );
    }, [applyCatalogueFallbackOnRow]);

    // Memoize the data with IDs to prevent unnecessary re-renders
    const tableData = useMemo(() => 
        mappedData.map(x => ({ ...x, id: x.__rowId }))
    , [mappedData]);

    const currentProfileKey = rows.length > 0 ? `${template.name}` : null;

    return (
        <>
            <ModalHeader toggle={toggle}>{template.name || "Import Template Details"}</ModalHeader>

            <ModalBody className="d-flex flex-column" style={{ minHeight: "400px" }}>
                <p>{template.description || "No description available."}</p>

                <Row className="align-items-center">
                    <Col className="d-flex align-items-center gap-2">
                        <Input
                            innerRef={fileInputRef}
                            type="file"
                            className="visually-hidden"
                            onChange={handleHiddenInputChange}
                        />
                        {!selectedFile ? (
                            <Button onClick={openPicker} disabled={isUploading}>
                                Choose file
                            </Button>
                        ) : (
                            <>
                                <span className="text-muted small">{selectedFile.name}</span>
                                <Button color="secondary" onClick={handleChangeFile} disabled={isUploading}>
                                    Change file
                                </Button>
                            </>
                        )}
                    </Col>

                    <Col className="d-flex justify-content-end">
                        <Button onClick={() => handleFileDownload(template.s3Key)} disabled={isUploading}>
                            Download Template
                        </Button>
                    </Col>
                </Row>

                <hr className="my-3" />

                {uploadError && (
                    <Alert color="danger" className="py-2">
                        {uploadError}
                    </Alert>
                )}

                {isUploading && (
                    <div className="d-flex align-items-center gap-2 text-muted">
                        <Spinner size="sm" /> <span>Uploading and parsing…</span>
                    </div>
                )}

                {rows.length > 0 ? (
                    <>
                        <Row className="align-items-center mb-2">
                            <Col xs="auto">
                                <Label className="form-label mb-0">Import Description*</Label>
                            </Col>
                            <Col xs="auto">
                                <Input
                                    type="text"
                                    value={groupName}
                                    maxLength={25}
                                    invalid={!groupName.trim()}
                                    style={{ width: "35ch" }}
                                    onChange={(e) => setGroupName(e.target.value.replace(/[^a-zA-Z0-9 ]/g, ""))}
                                    placeholder="Enter a group name for this import"
                                />
                            </Col>
                        </Row>
                        <br />
                        <DataTableWindowed
                            id={"import-preview-table-" + sanitizeForId(template.name)}
                            height="660px"
                            data={tableData}
                            columns={mappedColumns}
                            sortByColumn="__rowId"
                            sortDirection="ascending"
                            allowColumnCustomisation={true}
                            shrinkHeightToContents={true}
                            showGroupHeaders={true}
                            searchableFields={(mappedColumns || []).filter(x => !(["actions"].includes(x?.key))).map(c => c?.key)}
                            // Let the modal know which rows are currently filtered (unpaged)
                            onSelectedDataChange={(arr) => {
                                // We intentionally discard the row objects from `arr` and store only the IDs.
                                // The rows here come from DataTableWindowed's internal display-transformed data
                                // (customFilteredData), which can be stale at the point this callback fires —
                                // for example, immediately after a bulk save, the `data` prop change triggers
                                // this effect before customFilteredData has recomputed with the new values.
                                // By storing only IDs and deriving `selectedData` via useMemo from the live
                                // `quoteItems` prop, we guarantee the bulk modal always receives up-to-date rows.
                                setSelectedIds(new Set(arr.map(x => x.__rowId)));
                            }}
                            currentProfileKey={currentProfileKey}
                            actions={[
                                <Button
                                    key="update"
                                    color={theme === "dark-mode" ? "dark" : "light"}
                                    outline
                                    disabled={selectedData.length === 0}
                                    onClick={() => {
                                        // Gather selected mapped rows in current view
                                        openBulkWithItems(selectedData);
                                    }}
                                >
                                    Update Selected
                                </Button>
                            ]}
                            allowMultiSelect={true}
                        />
                    </>
                ) : !isUploading ? (
                    <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center text-muted text-center px-3">
                        <i className="bi bi-file-earmark-arrow-up fs-1 mb-3"></i>
                        <p className="fw-semibold mb-1">No file selected</p>
                        <p className="mb-0">
                            Please select a file that matches the <strong>{template.name || "import template"}</strong>.
                        </p>
                        <small className="text-muted">The file must follow the structure defined by this template.</small>
                    </div>
                ) : null}
                {isSavingQuoteItems && (
                    <div className="text-center mt-3">
                        <Spinner color="primary" />
                        <p className="text-muted mt-2">Saving items...</p>
                    </div>
                )}

            </ModalBody>

            <ModalFooter>
                <Button color={theme === "dark-mode" ? "dark" : "light"} outline onClick={toggle} disabled={isUploading || isSavingQuoteItems}>
                    Close
                </Button>
                <Button
                    color="secondary"
                    disabled={!rows.length || isUploading || isSavingQuoteItems}
                    onClick={handleImport}
                    title={!groupName.trim() ? "Enter a group name" : ""}
                >
                    Import
                </Button>
            </ModalFooter>

            <Modal isOpen={isSearchOpen} centered fullscreen toggle={closeSearch} size="xl">
                <ModalHeader toggle={closeSearch}>Search Catalogue</ModalHeader>
                <ModalBody>
                    <ItemsTable mode="import" onAddItemToImport={handleAddItem} />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeSearch}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            <QuoteItemBulkModal
                modalOpen={bulkModalOpen}
                setModalOpen={setBulkModalOpen}
                items={bulkItems}
                suppliers={suppliersData || []}
                itemTypes={itemTypes || []}
                onSave={handleBulkSave}
                showDetailsTab={false}
                showCommercialsTab={false}
                showImportTab={true}
                allowSetBillingType={true}
            />
        </>
    );
};

export default UserImportTemplateModal;
